import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useCreateEpic, useUpdateEpic } from "@/hooks/queries/use-epics";
import { useEpicRequests } from "@/hooks/queries/use-epic-requests";
import type { Epic } from "@/lib/api/types";
import type { EpicRequestWithReactionCounts } from "@/lib/api/epic-requests";
import { Check, FileText, ChevronRight, ArrowLeft, FolderTree } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DisciplinePicker } from "@/components/teams/discipline-picker";
import { useEpicOwnerTeams, useTeams } from "@/hooks/queries/use-teams";

interface EpicFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  epic?: Epic;
  defaultTeamId?: string;
}

type CreateStep = "select-request" | "configure";

export function EpicForm({ open, onOpenChange, epic, defaultTeamId }: EpicFormProps) {
  const isEditing = !!epic;
  const createEpic = useCreateEpic();
  const updateEpic = useUpdateEpic();
  const { data: epicOwnerTeams } = useEpicOwnerTeams();
  const { data: allTeamsResponse } = useTeams();

  // Create mode: governance-aware flow state
  const [step, setStep] = useState<CreateStep>("select-request");
  const [selectedRequest, setSelectedRequest] = useState<EpicRequestWithReactionCounts | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState(defaultTeamId ?? "");
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Form fields
  const [name, setName] = useState(epic?.name ?? "");
  const [description, setDescription] = useState(epic?.description ?? "");

  // Fetch approved epic requests for the selection step
  const { data: requestsData, isLoading: requestsLoading } = useEpicRequests(
    { status: "approved" }
  );

  // Flatten pages and filter out already-converted requests
  const approvedRequests = useMemo(() => {
    const allRequests = requestsData?.pages.flatMap((page) => page.data) ?? [];
    return allRequests.filter((request) => {
      if (request.status !== "approved" || request.convertedEpicId) {
        return false;
      }

      if (!selectedTeamId) {
        return true;
      }

      return request.teamId === selectedTeamId;
    });
  }, [requestsData, selectedTeamId]);

  const selectedTeam = useMemo(
    () => epicOwnerTeams?.find((team) => team.id === selectedTeamId),
    [epicOwnerTeams, selectedTeamId],
  );

  const projectChildren = useMemo(() => {
    const allTeams = allTeamsResponse?.data ?? [];
    return allTeams.filter(t => t.parentId === selectedTeamId && t.level === 'product');
  }, [allTeamsResponse, selectedTeamId]);

  // Reset form state when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (isEditing) {
        setName(epic?.name ?? "");
        setDescription(epic?.description ?? "");
      } else {
        setStep("select-request");
        setSelectedRequest(null);
        setSelectedTeamId(defaultTeamId ?? "");
        setSelectedProjectId("");
        setName("");
        setDescription("");
      }
    }
  }, [defaultTeamId, open, isEditing, epic]);

  // When a request is selected, pre-populate fields and move to configure step
  const handleSelectRequest = (request: EpicRequestWithReactionCounts) => {
    setSelectedRequest(request);
    setName(request.title);
    setDescription(
      request.structuredDesc?.proposedSolution ?? request.description ?? ""
    );
    setStep("configure");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      if (isEditing) {
        const updateData: Parameters<typeof updateEpic.mutateAsync>[0] = {
          id: epic.id,
          name: name.trim(),
        };
        if (description.trim()) updateData.description = description.trim();
        await updateEpic.mutateAsync(updateData);
      } else {
        if (!selectedTeamId) {
          console.error("No team ID selected");
          return;
        }
        if (!selectedRequest) {
          console.error("No epic request selected");
          return;
        }
        const createData: Parameters<typeof createEpic.mutateAsync>[0] = {
          name: name.trim(),
          teamId: selectedProjectId || selectedTeamId,
          epicRequestId: selectedRequest.id,
        };
        if (description.trim()) createData.description = description.trim();
        await createEpic.mutateAsync(createData);
      }
      onOpenChange(false);
      resetForm();
    } catch {
      // Error handling is done by the mutation
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedRequest(null);
    setSelectedTeamId(defaultTeamId ?? "");
    setSelectedProjectId("");
    setStep("select-request");
  };

  const isPending = createEpic.isPending || updateEpic.isPending;

  // ── Edit mode: simple form (unchanged behavior) ──
  if (isEditing) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Epic</DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Epic name"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !name.trim()}>
                {isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Create mode: governance-aware flow ──
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        {step === "select-request" && (
          <>
            <DialogHeader>
              <DialogTitle>New Epic</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Select an approved Epic Request to create an epic from.
              </p>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <DisciplinePicker
                value={selectedTeamId}
                onChange={(teamId) => {
                  setSelectedTeamId(teamId);
                  setSelectedProjectId("");
                  setSelectedRequest(null);
                }}
                label="Team / Project"
                description="Choose the discipline that will own the epic before selecting its approved request."
              />

              {projectChildren.length > 0 && (
                <div className="grid gap-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Project <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projectChildren.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!selectedTeamId ? (
                <div className="rounded-lg border border-dashed border-white/[0.08] px-4 py-6 text-center text-sm text-muted-foreground">
                  Select a discipline to load approved Epic Requests for that team.
                </div>
              ) : requestsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="h-16 animate-pulse rounded-lg bg-muted"
                    />
                  ))}
                </div>
              ) : approvedRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">
                    No approved Epic Requests available for {selectedTeam?.name ?? "this team"}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Create and get an Epic Request approved first.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to="/epic-requests"
                      onClick={() => onOpenChange(false)}
                    >
                      Go to Epic Requests
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                  {approvedRequests.map((request) => (
                    <button
                      key={request.id}
                      type="button"
                      onClick={() => handleSelectRequest(request)}
                      className={cn(
                        "w-full text-left rounded-lg border border-white/[0.06] bg-white/[0.02] p-3",
                        "hover:bg-white/[0.06] hover:border-white/[0.12] transition-colors",
                        "focus:outline-none focus:ring-2 focus:ring-primary/50",
                        "group"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="mb-1 flex items-center gap-2">
                            {request.identifier && (
                              <Badge
                                variant="outline"
                                className="text-xs font-mono shrink-0"
                              >
                                {request.identifier}
                              </Badge>
                            )}
                            <span className="text-sm font-medium truncate">
                              {request.title}
                            </span>
                          </div>
                          {request.structuredDesc?.problemStatement && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {request.structuredDesc.problemStatement}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:text-foreground transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "configure" && selectedRequest && (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>New Epic</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Configure your epic details. You can edit the name and description
                before creating.
              </p>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="flex flex-col gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Check className="h-4 w-4 shrink-0 text-green-400" />
                  <span className="text-xs text-muted-foreground">From request:</span>
                  <Badge variant="outline" className="text-xs font-mono">
                    {selectedRequest.identifier ?? "—"}
                  </Badge>
                  <span className="truncate text-xs">{selectedRequest.title}</span>
                </div>
                <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground sm:max-w-[45%]">
                  <FolderTree className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{selectedTeam?.fullPath ?? "No discipline selected"}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={() => {
                    setStep("select-request");
                    setSelectedRequest(null);
                    setName("");
                    setDescription("");
                  }}
                >
                  <ArrowLeft className="mr-1 h-3 w-3" />
                  Change
                </Button>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Epic name"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !name.trim() || !selectedTeamId}>
                {isPending ? "Creating..." : "Create Epic"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
